import { useMemo } from 'react';

import { useColorScheme } from '@/hooks/use-color-scheme';
import type { ResolvedTheme } from '@/store/themeStore';

// eslint-disable-next-line @typescript-eslint/no-require-imports
const palette = require('@/constants/palette') as {
  lightScales: Record<string, Record<string, string>>;
  darkScales: Record<string, Record<string, string>>;
  semantic: Record<ResolvedTheme, Record<string, string>>;
};

type Step = '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900' | '950';
type Scale = Record<Step, string>;

export interface ThemeColors {
  scheme: ResolvedTheme;
  isDark: boolean;
  background: string;
  foreground: string;
  card: string;
  border: string;
  primary: string;
  neutral: Scale;
  success: Scale;
  warning: Scale;
  error: Scale;
  info: Scale;
  premium: Scale;
}

function build(scheme: ResolvedTheme): ThemeColors {
  const scales = scheme === 'dark' ? palette.darkScales : palette.lightScales;
  const surfaces = palette.semantic[scheme];
  return {
    scheme,
    isDark: scheme === 'dark',
    background: surfaces.background,
    foreground: surfaces.foreground,
    card: surfaces.card,
    border: surfaces.border,
    primary: '#FD5001',
    neutral: scales.neutral as Scale,
    success: scales.success as Scale,
    warning: scales.warning as Scale,
    error: scales.error as Scale,
    info: scales.info as Scale,
    premium: scales.premium as Scale,
  };
}

const CACHE: Record<ResolvedTheme, ThemeColors> = { light: build('light'), dark: build('dark') };

/** Cores do tema atual, para props que só aceitam string. Prefira className. */
export function themeColors(scheme: ResolvedTheme): ThemeColors {
  return CACHE[scheme];
}

/**
 * Cores do tema atual para onde className não chega: cor de ícone,
 * `placeholderTextColor`, gráficos, estilos de bibliotecas.
 */
export function useThemeColors(): ThemeColors {
  const scheme = useColorScheme();
  return useMemo(() => CACHE[scheme], [scheme]);
}

/**
 * Hex do tema claro → token da paleta. É o que deixa um estilo inline antigo
 * (`borderColor: '#E5E5E5'`) acompanhar o tema sem reescrever a tela:
 * `borderColor: h('#E5E5E5')` devolve o mesmo hex no claro e o tom espelhado
 * no escuro.
 */
const HEX_TOKENS: Record<string, [string, string] | 'card' | 'ink'> = {
  '#ffffff': 'card',
  '#fff': 'card',
  '#fafafa': ['neutral', '50'],
  '#f9fafb': ['neutral', '50'],
  '#f5f5f5': ['neutral', '100'],
  '#f4f4f5': ['neutral', '100'],
  '#f3f4f6': ['neutral', '100'],
  '#e5e5e5': ['neutral', '200'],
  '#e5e7eb': ['neutral', '200'],
  '#e4e4e7': ['neutral', '200'],
  '#d4d4d4': ['neutral', '300'],
  '#d1d5db': ['neutral', '300'],
  '#a3a3a3': ['neutral', '400'],
  '#a1a1aa': ['neutral', '400'],
  '#9ca3af': ['neutral', '400'],
  '#737373': ['neutral', '500'],
  '#71717a': ['neutral', '500'],
  '#6b7280': ['neutral', '500'],
  '#525252': ['neutral', '600'],
  '#4b5563': ['neutral', '600'],
  '#404040': ['neutral', '700'],
  '#374151': ['neutral', '700'],
  '#262626': ['neutral', '800'],
  '#171717': ['neutral', '900'],
  '#18181b': ['neutral', '900'],
  '#111827': ['neutral', '900'],
  '#111': ['neutral', '900'],
  '#0a0a0a': ['neutral', '900'],
  '#09090b': ['neutral', '900'],
  '#000': ['neutral', '900'],
  '#000000': ['neutral', '900'],
  '#ecfdf5': ['emerald', '50'],
  '#047857': ['emerald', '700'],
  '#f0fdf4': ['green', '50'],
  '#fff7ed': ['orange', '50'],
  '#c2410c': ['orange', '700'],
  '#fff4ed': ['primary', '50'],
  '#fef2f2': ['red', '50'],
  '#fecaca': ['red', '200'],
  '#b91c1c': ['red', '700'],
  '#fffbeb': ['amber', '50'],
  '#fef3c7': ['amber', '100'],
  '#b45309': ['amber', '700'],
};

export function themedHex(hex: string, scheme: ResolvedTheme): string {
  if (scheme === 'light') return hex;
  const token = HEX_TOKENS[hex.toLowerCase()];
  if (!token) return hex;
  if (token === 'card' || token === 'ink') return palette.semantic.dark[token];
  return palette.darkScales[token[0]]?.[token[1]] ?? hex;
}

/** `const h = useThemedHex()` → `h('#E5E5E5')`. Ver `themedHex`. */
export function useThemedHex(): (hex: string) => string {
  const scheme = useColorScheme();
  return useMemo(() => (hex: string) => themedHex(hex, scheme), [scheme]);
}

/**
 * Estilos de módulo (StyleSheet, mapas de variante) que dependem do tema:
 * `const useStyles = createThemedStyles((th) => StyleSheet.create({...}))` e,
 * no componente, `const styles = useStyles()`. Cada tema é montado uma vez só.
 */
export function createThemedStyles<T>(factory: (th: (hex: string) => string) => T): () => T {
  const cache: Partial<Record<ResolvedTheme, T>> = {};
  return function useThemedStyles() {
    const scheme = useColorScheme();
    if (!cache[scheme]) cache[scheme] = factory((hex) => themedHex(hex, scheme));
    return cache[scheme] as T;
  };
}

/**
 * Círculo de ícone com cor vinda de JS (cor da marca do prestador, status):
 * no claro a tinta translúcida de sempre (`${cor}${alpha}`), no escuro a cor
 * cheia com ícone branco — o mesmo que `bg-primary/10 dark:bg-primary`.
 */
export function useIconChip(): (color: string, alpha?: string) => { bg: string; fg: string } {
  const scheme = useColorScheme();
  return useMemo(
    () => (color: string, alpha = '1A') =>
      scheme === 'dark' ? { bg: color, fg: '#FFFFFF' } : { bg: `${color}${alpha}`, fg: color },
    [scheme],
  );
}

/**
 * Círculo de ícone de status (config com `bg` + `color`): no claro fica como
 * está; no escuro o fundo vira a cor do ícone e o ícone fica branco.
 * `chip.cls(bgClass)` / `chip.bg(lightHex, color)` para o fundo,
 * `chip.style(color)` e `chip.icon(color)` para o resto.
 */
export function useSolidChip() {
  const isDark = useColorScheme() === 'dark';
  return useMemo(
    () => ({
      cls: (bgClass: string) => (isDark ? '' : bgClass),
      style: (color: string) => (isDark ? { backgroundColor: color } : undefined),
      bg: (lightBg: string, color: string) => (isDark ? color : lightBg),
      icon: (color: string) => (isDark ? '#FFFFFF' : color),
    }),
    [isDark],
  );
}
